import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import './Auth.css';

export default function AuthPage() {
    const auth = useAuth();
    const navigate = useNavigate();
    const [mode, setMode] = useState('signin'); // signin, signup, confirm, forgot, resetPassword
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');

    const handleSignIn = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage('');
        try {
            await auth.signIn(email, password);
            navigate('/');
        } catch (err) {
            // error is set in useAuth
        } finally {
            setLoading(false);
        }
    };

    const handleSignUp = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage('');
        try {
            await auth.signUp(email, password);
            setMode('confirm');
            setMessage('Check your email for a verification code.');
        } catch (err) {
            // error is set in useAuth
        } finally {
            setLoading(false);
        }
    };

    const handleConfirm = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage('');
        try {
            await auth.confirmSignUp(email, code);
            setMessage('Verified. Signing you in...');
            await auth.signIn(email, password);
            navigate('/');
        } catch (err) {
            // error is set in useAuth
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage('');
        try {
            await auth.forgotPassword(email);
            setMode('resetPassword');
            setMessage('Check your email for a reset code.');
        } catch (err) {
            // error is set in useAuth
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage('');
        try {
            await auth.confirmPassword(email, code, newPassword);
            setMessage('Password reset. Signing you in...');
            await auth.signIn(email, newPassword);
            navigate('/');
        } catch (err) {
            // error is set in useAuth
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-page">
            <div className="auth-card">
                <div className="auth-brand">
                    <span className="auth-brand-dot" />
                    InTension
                </div>

                {mode === 'signin' && (
                    <form onSubmit={handleSignIn}>
                        <h2>Sign in</h2>
                        <input
                            type="email"
                            placeholder="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            autoFocus
                        />
                        <input
                            type="password"
                            placeholder="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                        <button type="submit" className="auth-btn" disabled={loading}>
                            {loading ? 'Signing in...' : 'Sign in'}
                        </button>
                        <div className="auth-links">
                            <button type="button" onClick={() => { setMode('signup'); auth.setError(null); setMessage(''); }}>
                                Create an account
                            </button>
                            <button type="button" onClick={() => { setMode('forgot'); auth.setError(null); setMessage(''); }}>
                                Forgot password?
                            </button>
                        </div>
                    </form>
                )}

                {mode === 'signup' && (
                    <form onSubmit={handleSignUp}>
                        <h2>Create account</h2>
                        <input
                            type="email"
                            placeholder="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            autoFocus
                        />
                        <input
                            type="password"
                            placeholder="Password (min 8 chars, include a number)"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={8}
                        />
                        <button type="submit" className="auth-btn" disabled={loading}>
                            {loading ? 'Creating...' : 'Create account'}
                        </button>
                        <div className="auth-links">
                            <button type="button" onClick={() => { setMode('signin'); auth.setError(null); setMessage(''); }}>
                                Already have an account? Sign in
                            </button>
                        </div>
                    </form>
                )}

                {mode === 'confirm' && (
                    <form onSubmit={handleConfirm}>
                        <h2>Verify your email</h2>
                        <p className="auth-subtitle">Enter the code sent to {email}</p>
                        <input
                            type="text"
                            placeholder="Verification code"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            required
                            autoFocus
                            autoComplete="one-time-code"
                        />
                        <button type="submit" className="auth-btn" disabled={loading}>
                            {loading ? 'Verifying...' : 'Verify'}
                        </button>
                    </form>
                )}

                {mode === 'forgot' && (
                    <form onSubmit={handleForgotPassword}>
                        <h2>Reset password</h2>
                        <input
                            type="email"
                            placeholder="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            autoFocus
                        />
                        <button type="submit" className="auth-btn" disabled={loading}>
                            {loading ? 'Sending...' : 'Send reset code'}
                        </button>
                        <div className="auth-links">
                            <button type="button" onClick={() => { setMode('signin'); auth.setError(null); setMessage(''); }}>
                                Back to sign in
                            </button>
                        </div>
                    </form>
                )}

                {mode === 'resetPassword' && (
                    <form onSubmit={handleResetPassword}>
                        <h2>Enter new password</h2>
                        <p className="auth-subtitle">Enter the code sent to {email}</p>
                        <input
                            type="text"
                            placeholder="Reset code"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            required
                            autoFocus
                            autoComplete="one-time-code"
                        />
                        <input
                            type="password"
                            placeholder="New password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                            minLength={8}
                        />
                        <button type="submit" className="auth-btn" disabled={loading}>
                            {loading ? 'Resetting...' : 'Reset password'}
                        </button>
                    </form>
                )}

                {auth.error && <div className="auth-error">{auth.error}</div>}
                {message && <div className="auth-message">{message}</div>}

                <button type="button" className="auth-skip" onClick={() => navigate('/')}>
                    Skip for now
                </button>
            </div>
        </div>
    );
}
